import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";

/** Toca um beep curto atraves da Web Audio API (sem ficheiro). */
function playBeep(pattern = "urgent") {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    const notes = pattern === "urgent"
      ? [{ f: 880, t: 0 }, { f: 660, t: 0.15 }, { f: 880, t: 0.3 }]
      : [{ f: 660, t: 0 }];
    notes.forEach(({ f, t }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.15, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + t + 0.14);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.18);
    });
    setTimeout(() => ctx.close?.(), 900);
  } catch { /* silent */ }
}

/**
 * Escuta em background novos handoffs (poll a cada 20s). Quando chega um novo,
 * mostra banner + toca beep. Se for URGENTE (SLA < 15min ou ja atrasado), o
 * banner e persistente ate o utilizador clicar 'Ver'.
 */
export default function LiveHandoffAlerts() {
  const [urgentBanner, setUrgentBanner] = useState(null);
  const lastSeenRef = useRef(new Date().toISOString());
  const audioEnabledRef = useRef(true);
  const { user } = useAuth();
  const authed = !!user;
  const canRead = !!user?.roles?.some((r) => ["super_admin", "admin", "comercial", "tecnico", "suporte", "marketing_analyst", "operacoes"].includes(r));

  useEffect(() => {
    if (!authed || !canRead) return;
    let stopped = false;
    // Unlock AudioContext no primeiro clique do utilizador
    const unlock = () => { try { new (window.AudioContext || window.webkitAudioContext)().resume?.(); } catch { /* silent */ } document.removeEventListener("click", unlock); };
    document.addEventListener("click", unlock, { once: true });

    const poll = async () => {
      try {
        const { data } = await api.get("/chat-ai/handoffs", {
          params: { since: lastSeenRef.current, estado: "todos" },
        });
        const items = data?.handoffs || [];
        if (items.length > 0) {
          // Actualiza cursor para o mais recente
          const latest = items.reduce((max, h) => h.criado_em > max ? h.criado_em : max, lastSeenRef.current);
          lastSeenRef.current = latest;
          const urgent = items.find((h) => ["atrasado", "urgente"].includes(h?.sla?.badge));
          if (urgent) {
            setUrgentBanner({ ...urgent, count: items.length });
            if (audioEnabledRef.current) playBeep("urgent");
          } else {
            if (audioEnabledRef.current) playBeep("normal");
          }
          // Toast breve por cada handoff (max 3)
          items.slice(0, 3).forEach((h) => {
            const tipoLabel = h.tipo === "tecnico" ? "Tecnico" : "Comercial";
            const origemLabel = { "chat-ai": "Chat", "formulario": "Formulario", "email": "Email" }[h.origem] || h.origem;
            toast(`Novo handoff ${tipoLabel} (${origemLabel})`, {
              description: `${h.nome || "Visitante"} - ${h.plataforma || "sem plataforma"}`,
              action: { label: "Ver", onClick: () => (window.location.href = "/app/handoffs") },
            });
          });
        }
      } catch { /* silent */ }
    };
    // Primeira chamada apos 5s (deixa a app estabilizar)
    const first = setTimeout(poll, 5000);
    const timer = setInterval(() => { if (!stopped) poll(); }, 20000);
    return () => { stopped = true; clearTimeout(first); clearInterval(timer); document.removeEventListener("click", unlock); };
  }, [authed, canRead]);

  if (!urgentBanner) return null;
  const isBreach = urgentBanner?.sla?.badge === "atrasado";
  return (
    <div
      className={`fixed top-3 right-3 z-[1000] max-w-sm border-2 rounded-lg shadow-2xl p-3 animate-pulse ${isBreach ? "bg-red-50 border-red-500" : "bg-orange-50 border-orange-500"}`}
      data-testid="live-handoff-banner"
    >
      <div className="flex items-start gap-2">
        {isBreach
          ? <AlertTriangle size={20} className="text-red-600 shrink-0 mt-0.5" />
          : <Zap size={20} className="text-orange-600 shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className={`text-xs font-bold uppercase ${isBreach ? "text-red-700" : "text-orange-700"}`}>
            {isBreach ? "SLA ULTRAPASSADO" : "Handoff Urgente"}
          </div>
          <div className="text-sm font-semibold text-navy truncate">{urgentBanner.nome || "Visitante"}</div>
          <div className="text-[11px] text-slate-600 truncate">{urgentBanner.tipo === "tecnico" ? "Suporte tecnico" : "Comercial"} - {urgentBanner.plataforma || "sem plataforma"}</div>
          {urgentBanner.count > 1 && <div className="text-[10px] text-slate-500 mt-0.5">+{urgentBanner.count - 1} outro(s) pedido(s) novos</div>}
          <div className="flex gap-2 mt-2">
            <Link to="/app/handoffs" onClick={() => setUrgentBanner(null)} className="text-[11px] font-semibold px-2 py-1 rounded bg-navy text-white hover:bg-navy/90" data-testid="live-handoff-goto">Ir para a fila</Link>
            <button onClick={() => setUrgentBanner(null)} className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100" data-testid="live-handoff-dismiss">Dispensar</button>
            <button onClick={() => { audioEnabledRef.current = !audioEnabledRef.current; toast(`Som ${audioEnabledRef.current ? "activado" : "silenciado"} para esta sessao.`); }} className="text-[10px] text-slate-500 underline ml-auto self-center" data-testid="live-handoff-mute">
              {audioEnabledRef.current ? "Silenciar" : "Activar som"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
